/** Helper data per i filtri della lista ordini (range default). */

/** Data locale in ISO yyyy-mm-dd, il formato di ``<input type="date">``.
 *
 *  Costruita dai componenti LOCALI e non via ``toISOString()``: quest'ultima
 *  converte in UTC, quindi a Roma (UTC+1/+2) la mezzanotte locale retrocede al
 *  giorno prima e il range partirebbe con un giorno di sfasamento. */
function toIsoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Range data di default per la lista ordini.
 *
 * - ``from`` = 30 giorni prima di ``today``.
 * - ``to`` = **vuota** (nessun limite superiore).
 *
 * Perché ``to`` vuota: il limite superiore veniva calcolato una sola volta
 * all'apertura dell'app e NON avanzava a mezzanotte. Con l'app aperta a
 * cavallo della mezzanotte, gli ordini del giorno nuovo cadevano fuori dal
 * filtro "Data a" e sparivano dalla lista finché non si riavviava (bug
 * 2026-06-04, ordine 407-0641737-8872358). Lasciando ``to`` vuota la lista
 * mostra sempre gli ordini fino ad oggi; ``refresh()``/``syncNow()`` omettono
 * ``date_to`` quando vuota (il backend usa "oggi" come default).
 *
 * ``today`` è iniettabile per i test.
 */
export function defaultDateRange(today: Date = new Date()): { from: string; to: string } {
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  return { from: toIsoDate(from), to: "" };
}

/** Id dei periodi rapidi del calendario (Davide 2026-09-23). */
export type PeriodoId = "oggi" | "7g" | "30g" | "90g" | "anno" | "tutto";

/** Periodi rapidi, nell'ordine del menu. Tutti con la data finale VUOTA, come
 *  il default: arrivano sempre a oggi e non si congelano a mezzanotte. */
export const PERIODI_RAPIDI: ReadonlyArray<{ id: PeriodoId; label: string }> = [
  { id: "oggi", label: "Oggi" },
  { id: "7g", label: "Ultimi 7 giorni" },
  { id: "30g", label: "Ultimi 30 giorni" },
  { id: "90g", label: "Ultimi 90 giorni" },
  { id: "anno", label: "Ultimo anno" },
  { id: "tutto", label: "Tutto il periodo" },
];

const GIORNI: Partial<Record<PeriodoId, number>> = { oggi: 0, "7g": 7, "30g": 30, "90g": 90 };

/** Le date di un periodo rapido. «Ultimo anno» torna allo stesso giorno
 *  dell'anno prima (non 365 giorni: con un febbraio bisestile di mezzo
 *  perderebbe un giorno); «Tutto il periodo» toglie entrambi i limiti. */
export function periodoRapido(
  id: PeriodoId,
  today: Date = new Date(),
): { from: string; to: string } {
  if (id === "tutto") return { from: "", to: "" };
  const from = new Date(today);
  if (id === "anno") from.setFullYear(from.getFullYear() - 1);
  else from.setDate(from.getDate() - (GIORNI[id] ?? 0));
  return { from: toIsoDate(from), to: "" };
}

/** Il periodo rapido che corrisponde alle date impostate, o `null` se sono
 *  su misura. Serve a dare un NOME al filtro («Ultimi 30 giorni») invece di
 *  due date da decifrare. */
export function periodoCorrente(
  from: string,
  to: string,
  today: Date = new Date(),
): PeriodoId | null {
  const trovato = PERIODI_RAPIDI.find((p) => {
    const date = periodoRapido(p.id, today);
    return date.from === from && date.to === to;
  });
  return trovato?.id ?? null;
}
